import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { Confetti, ConfettiRef } from '@/components/ui/confetti';
import { useActivationCompletion } from '@/hooks/use-activation-completion';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { APP_ROUTE } from '@/constants';
import { QuickStartActiveStepState, useDestinations } from '@/states';
import { ClientContainer, UserContainer } from '@/providers';
import { trackEvent } from '@/helpers/external-tools-helper';
import { ACTIVATION_ANALYTICS_EVENTS } from '@/analytics/activation';
import useTemplates from '@/hooks/use-templates';

interface ActionItemProps {
  label: string;
  isCompleted?: boolean;
  isActive?: boolean;
  onClick?: () => void;
  description?: string;
}

const ActionItem: React.FC<ActionItemProps> = ({
  label,
  isCompleted,
  isActive,
  onClick,
  description,
}) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          className={cn(
            'flex items-center gap-2 py-1 px-1.5 rounded-md transition-colors text-xs font-medium w-full',
            isCompleted
              ? 'text-gray-400 cursor-default'
              : 'text-gray-800 cursor-pointer hover:bg-gray-50',
            isActive ? 'bg-gray-50' : ''
          )}
          onClick={isCompleted ? undefined : onClick}
          disabled={isCompleted}
        >
          <span className="inline-flex items-center justify-center w-4 h-4">
            <i
              className={`ti ${
                isCompleted
                  ? 'ti-circle-check text-green-400'
                  : 'ti-circle-check text-gray-400'
              } text-[13px]`}
            />
          </span>
          <span
            className={cn('truncate text-left', isCompleted && 'line-through')}
          >
            {label}
          </span>
        </button>
      </TooltipTrigger>
      {description && !isCompleted && (
        <TooltipContent side="right" className="max-w-[200px]">
          <p className="text-xs">{description}</p>
        </TooltipContent>
      )}
    </Tooltip>
  </TooltipProvider>
);

const ActivationFlowSidebar = () => {
  const navigate = useNavigate();

  // State
  const [activeStep, setActiveStep] = useRecoilState(QuickStartActiveStepState);
  const { quickStart } = useActivationCompletion(); // Use centralized hook

  // Confetti state
  const confettiRef = useRef<ConfettiRef>(null);
  const [previousQuickStart, setPreviousQuickStart] = useState(quickStart);

  // Containers
  const { state: clientState } = ClientContainer.useContainer();
  const { state: userState } = UserContainer.useContainer();

  // Hooks
  const { getDestinations } = useDestinations();
  const { getTemplates } = useTemplates();

  const completedSteps = Object.values(quickStart).filter(
    step => step === 'completed'
  ).length;
  const totalSteps = Object.keys(quickStart).length;
  const progress = Math.round((completedSteps / totalSteps) * 100);

  useEffect(() => {
    getDestinations();
    getTemplates();
  }, []);

  const triggerSideCannons = useCallback(() => {
    const colors = [
      '#FF3D7F',
      '#FF9A3D',
      '#FFE23D',
      '#3DFF7F',
      '#3DFFF5',
      '#3D7FFF',
      '#9A3DFF',
      '#FF3DD1',
      '#3DFF9A',
      '#FF573D',
    ];
    // This setTimeout is a workaround to fix the issue with confetti not being triggered programmatically
    setTimeout(() => {
      confettiRef.current?.fire({
        particleCount: 30,
        angle: 60,
        spread: 35,
        startVelocity: 30,
        origin: { x: 0, y: 1 },
        colors,
        scalar: 0.6,
      });

      confettiRef.current?.fire({
        particleCount: 30,
        angle: 120,
        spread: 35,
        startVelocity: 30,
        origin: { x: 1, y: 1 },
        colors,
        scalar: 0.6,
      });
    }, 100);
  }, [confettiRef]);

  useEffect(() => {
    const stepKeys = Object.keys(quickStart) as Array<keyof typeof quickStart>;
    const hasNewCompletion = stepKeys.some(key => {
      const previousValue = previousQuickStart[key];
      const currentValue = quickStart[key];

      return currentValue === 'completed' && previousValue !== 'completed';
    });

    if (hasNewCompletion) {
      triggerSideCannons();
    }

    setPreviousQuickStart(quickStart);
  }, [previousQuickStart, quickStart, triggerSideCannons]);

  const navigateToStep = useCallback(
    (step: keyof typeof quickStart) => {
      const pathMap = {
        account: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.DASHBOARD}`,
        datasources: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DATA_SOURCES}/${APP_ROUTE.ADD}`,
        destination: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.CONNECTIONS}/${APP_ROUTE.DESTINATION}`,
        report: `/ws/${clientState.selectedClient?.id}/${APP_ROUTE.REPORTING}/${APP_ROUTE.REPORTS}`,
      };

      const path = pathMap[step];
      if (path) {
        navigate(path);
      }
    },
    [navigate, clientState.selectedClient?.id]
  );

  const handleNavigateToDataSources = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DATASOURCE_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('datasources');
  };

  const handleNavigateToDestination = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_ADD_DESTINATION_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('destination');
  };

  const handleNavigateToReports = () => {
    trackEvent(
      ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_CREATE_REPORT_STARTED,
      {
        businessId: userState.business?.id,
        clientId: clientState.selectedClient?.id,
      }
    );
    navigateToStep('report');
  };

  const handleStepClick = (stepKey: keyof typeof quickStart) => {
    if (quickStart[stepKey] === 'completed') return;

    setActiveStep(stepKey as any);

    if (stepKey === 'datasources') {
      handleNavigateToDataSources();
    } else if (stepKey === 'destination') {
      handleNavigateToDestination();
    } else if (stepKey === 'report') {
      handleNavigateToReports();
    }
  };

  return (
    <div className="relative overflow-hidden">
      <div className="w-full bg-white rounded-lg border border-gray-100 p-3">
        <div className="space-y-3">
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-gray-900">
                Getting Started
              </span>
              <span className="text-[11px] font-medium text-gray-500">
                {progress}%
              </span>
            </div>
            <div className="h-1 bg-gray-100 rounded-full overflow-hidden mb-1">
              <div
                className="h-full bg-green-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
                role="progressbar"
                aria-label="Setup completion progress"
                aria-valuenow={progress}
                aria-valuemin={0}
                aria-valuemax={100}
              />
            </div>
          </div>
          {/* Steps */}
          <div className="space-y-0.5">
            <ActionItem
              label="Setup account"
              isCompleted={quickStart.account === 'completed'}
              isActive={activeStep === 'account'}
              onClick={() => handleStepClick('account')}
              description="Complete your account profile and business information"
            />
            <ActionItem
              label="Add data sources"
              isCompleted={quickStart.datasources === 'completed'}
              isActive={activeStep === 'datasources'}
              onClick={() => handleStepClick('datasources')}
              description="Connect your marketing platforms (social media, ads, email, website)"
            />
            <ActionItem
              label="Connect Google Drive"
              isCompleted={quickStart.destination === 'completed'}
              isActive={activeStep === 'destination'}
              onClick={() => handleStepClick('destination')}
              description="Connect Google Drive to save and export reports as Google Slides/Sheets"
            />
            <ActionItem
              label="Create report"
              isCompleted={quickStart.report === 'completed'}
              isActive={activeStep === 'report'}
              onClick={() => handleStepClick('report')}
              description="Create your first performance report with your connected data"
            />
          </div>
          {/* Complete Setup Button */}
          <div className="flex justify-end pt-1">
            <button
              className="text-xs text-primary hover:text-primary/90 font-medium"
              onClick={() => {
                const firstIncompleteStep = Object.entries(quickStart).find(
                  ([_, status]) => status !== 'completed'
                )?.[0] as keyof typeof quickStart;

                if (firstIncompleteStep) {
                  navigateToStep(firstIncompleteStep);
                  trackEvent(
                    ACTIVATION_ANALYTICS_EVENTS.ACTIVATION_FLOW_STARTED,
                    {
                      businessId: userState.business?.id,
                      clientId: clientState.selectedClient?.id,
                    }
                  );
                }
              }}
            >
              Complete setup
            </button>
          </div>
        </div>
      </div>
      <Confetti
        ref={confettiRef}
        className="absolute inset-0 top-auto pointer-events-none"
        manualstart
      />
    </div>
  );
};

export default ActivationFlowSidebar;
